import AxiosHttpClient from 'app/api/AxiosHttpClient';
import { CardOrder } from 'app/model/CardOrder';
import { DocumentReference } from 'app/model/DocumentReference';
import { ResourceFormat } from 'app/model/ResourceFormat';
import { OtpConfirmation } from 'app/model/OtpConfirmation';
import { FilesUtils } from 'app/utils/FilesUtils';
import { OtpSign } from 'app/model/OtpSign';
import { ApiError } from 'app/api/exception/ApiError';
import { CardOrderCancelReason } from 'app/model/CardOrderCancelReason';
import Big from 'big.js';

/**
 * Сервис работы с заявкой на кредитную карту.
 */
export class OrderService {
    private readonly ORDERS_PATH = '/orders';

    private readonly ORDER_ID_PATH = (id: string) => `/orders/${id}`;

    private readonly DOCUMENTS_PATH = (id: string) =>
        `/orders/${id}/confirmation/documents`;

    private readonly PERSONALIZE_PATH = (id: string) =>
        `/orders/${id}/personalize`;

    private readonly DOCUMENT_CONTENT_PATH = (id: string, code: string) =>
        `/orders/${id}/confirmation/document/${code}`;

    private readonly DOCUMENT_CONFIRMATION_COMPLETE = (id: string) =>
        `/orders/${id}/complete`;

    private readonly GET_OTP_CONFIRMATION = (id: string) =>
        `/orders/${id}/confirmation/otp`;

    private readonly CANCEL_ORDER = (id: string) => `/orders/${id}/cancel`;

    private readonly EMPLOYMENT_DETAILS_PATH = () => '/user/salary-info';

    private apiClient: AxiosHttpClient = new AxiosHttpClient();

    /**
     * Получить заявку на кредитную карту по идентификатору
     * @param id идентификатор заявки на кредитную карту
     */
    async getById(id: string): Promise<CardOrder> {
        return CardOrder.deserialize(
            await this.apiClient.executeGetRequest(this.ORDER_ID_PATH(id)),
        );
    }

    /**
     * Создать заявку на кредитную карту
     *
     * @param order модель заявки на кредитную карту
     */
    async createOrder(order: CardOrder): Promise<CardOrder> {
        return CardOrder.deserialize(
            await this.apiClient.executePostRequest(this.ORDERS_PATH, order),
        );
    }

    /**
     * Обновить заявку на кредитную карту
     *
     * @param order модель заявки на кредитную карту
     */
    async updateOrder(order: CardOrder): Promise<CardOrder> {
        return CardOrder.deserialize(
            await this.apiClient.executePutRequest(
                this.ORDER_ID_PATH(order.id),
                order,
            ),
        );
    }

    /**
     * Перосонализировать заявку. Заполнить заявку личными данными пользователя.
     * @param order заявка на кредитную карту
     */
    async personalize(order: CardOrder): Promise<CardOrder> {
        return CardOrder.deserialize(
            await this.apiClient.executePutRequest(
                this.PERSONALIZE_PATH(order.id),
                order,
            ),
        );
    }

    /**
     * Если клиент является зарплатным, возвращает среднюю зарплату за последние 3 месяца и УНП.
     * Если зарплатных источников несколько, возвращает их сумму,
     * при этом УНП будет соответствать источнику с большим значением средний зарплаты
     * @returns информаця о зарплате и УНП клиента
     */
    async getEmploymentDetails(): Promise<{
        unp: null | string;
        incomeAmount: null | Big;
    }> {
        const result = await this.apiClient.executeGetRequest(
            this.EMPLOYMENT_DETAILS_PATH(),
        );

        return {
            unp: result.unp,
            incomeAmount: result.incomeAmount
                ? new Big(result.incomeAmount)
                : null,
        };
    }

    /**
     * Получить ссылки документы для подтверждения заявки.
     * @param id идентификатор заявки
     */
    async getOrderDocuments(id: string): Promise<DocumentReference[]> {
        const documentReferences = (await this.apiClient.executeGetRequest(
            this.DOCUMENTS_PATH(id),
        )) as DocumentReference[];

        if (documentReferences.length) {
            return documentReferences;
        } else {
            throw new ApiError();
        }
    }

    /**
     * Получить ссылки документы для подтверждения заявки в виде ZIP архива.
     * @param id идентификатор заявки
     */
    async getZipArchive(id: string): Promise<{ file: Blob; filename: string }> {
        const response = await this.apiClient.executeDownload(
            this.DOCUMENTS_PATH(id),
            { asSingleZip: true },
        );
        return FilesUtils.getFileInformation(response, ResourceFormat.ZIP);
    }

    /**
     * Получить содержимое документа по ссылке
     * @param orderId идентификатор заявки
     * @param code уникальный код документа
     * @param format формат скачиваемого файла (по умолчанию  {@link ResourceFormat#PDF})
     */
    async getDocumentResource(
        orderId: string,
        code: string,
        format: ResourceFormat,
    ): Promise<{ file: Blob; filename: string }> {
        const response = await this.apiClient.executeDownload(
            this.DOCUMENT_CONTENT_PATH(orderId, code),
            { format },
        );
        return FilesUtils.getFileInformation(response, format);
    }

    /**
     * Исполнить заявку на карту.
     * @param orderId идентификатор подписанной заявки.
     * @param otpInfo данные о подтверждении методом OTP.
     */
    async completeOrder(orderId: string, otpInfo: OtpSign): Promise<void> {
        return this.apiClient.executePostRequest(
            this.DOCUMENT_CONFIRMATION_COMPLETE(orderId),
            otpInfo,
        );
    }

    /**
     * Запросить OTP код для подтверждения заявки
     * @param orderId идентификатор заявки
     */
    async getOrderConfirmationOtp(orderId: string): Promise<OtpConfirmation> {
        return this.apiClient.executeGetRequest(
            this.GET_OTP_CONFIRMATION(orderId),
        );
    }

    /**
     * Аннулировать заявку
     * @param orderId идентификатор заявки
     * @param reason причина аннулирования заявки
     */
    async cancelOrder(
        orderId: string,
        reason: CardOrderCancelReason,
    ): Promise<void> {
        return this.apiClient.executePostRequest(this.CANCEL_ORDER(orderId), {
            reason,
        });
    }

    /**
     * Получить список ранее созданных заявок
     */
    async getDraftOrders(): Promise<any> {
        return this.apiClient.executeGetRequest(this.ORDERS_PATH);
    }
}
